import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/apiService';
import { Btn, Loading, Msg, Section } from './kit';
import {
  FEATURE_KEYS, FEATURE_LABELS, FEATURE_MODE_LABELS, FEATURE_OPEN_URL, announceFeatureAccessChange,
  featureModeOf, type FeatureKey, type FeatureMode,
} from '../../utils/featureAccess';
import {
  AdminPicker, ChangedLine, ModeSwitch, Tag, emailsOf, sameList, useAdminRoster, type AdminListState, type AdminOption,
} from './accessKit';

/**
 * The "Feature access" tab (super admins only): who may open a member page
 * while it is tested. For You & What's New shows the For You (new) and
 * What's New rows (only={['foryou', 'whatsnew']}), Ocean shows OCEAN's
 * (only={['ocean']}). Both tabs are the super-admin-only sub-tabs
 * "foryou_whatsnew.feature_access" / "ocean.feature_access"
 * (admin/adminAccess.tsx SUPER_ONLY_TABS): never shown to other admins,
 * whatever their access to the group.
 *
 *   Super admins only  (default) only super admins see the page, with a
 *                      "Super admin preview" badge
 *   Selected admins    super admins plus the admins picked here ("Preview")
 *   All admins         every admin ("Preview")
 *   Everyone           every signed-in account (asks for confirmation)
 *
 * Until a page is open to everyone, members keep the previous For You page /
 * no What's New / the OCEAN Coming Soon page, and the server refuses the
 * feature's API for them (403 feature_not_enabled).
 *
 * Backend/feature_access.py (app_settings 'feature_access', audit-logged),
 * through the panel dispatcher, section "system" (super admins only):
 *   GET  system/feature_access       {features: {key: {label, view, mode, admins, updated_by, updated_at}}}
 *   POST system/feature_access_set   {feature, mode, admins?}  (admins only with "selected")
 * The picker's admin list comes from access/access_state (useAdminRoster)
 * unless the caller passes `admins`. The app re-reads its own access after a
 * change (utils/featureAccess.ts).
 */

const SECTION = 'system';
const uid = () => apiService.getStoredUserId() || '';

async function faGet(): Promise<{ data: any; error: string }> {
  const r = await apiService.adminPanelGet(uid(), SECTION, 'feature_access');
  if (r.success && r.data?.success) return { data: r.data.data, error: '' };
  if (r.status === 404) return { data: null, error: 'This server has no feature access controls yet.' };
  return { data: null, error: r.data?.detail || r.error || 'Request failed' };
}

async function faSet(feature: FeatureKey, mode: FeatureMode, admins: string[]): Promise<string> {
  // `admins` only with Selected: left out, the server keeps the stored list.
  const r = await apiService.adminPanelPost(uid(), SECTION, 'feature_access_set',
    { feature, mode, ...(mode === 'selected' ? { admins } : {}) });
  if (r.success && r.data?.success) return '';
  return r.data?.detail || r.error || 'Request failed';
}

interface Row {
  key: FeatureKey; label: string; mode: FeatureMode; admins: string[];
  updatedBy: string; updatedAt: string;
}

const str = (v: any) => (typeof v === 'string' ? v : '');

/** The server's features, as a map {key: {...}} or a list [{key, ...}].
 *  Anything missing reads as the default (super admins only, never changed). */
function readRows(data: any): Row[] {
  const d = data && typeof data === 'object' ? data : {};
  const src = d.features;
  const byKey: Record<string, any> = {};
  if (Array.isArray(src)) src.forEach((f: any) => { if (f && typeof f.key === 'string') byKey[f.key] = f; });
  else if (src && typeof src === 'object') Object.assign(byKey, src);
  return FEATURE_KEYS.map(k => {
    const f = byKey[k] && typeof byKey[k] === 'object' ? byKey[k] : {};
    return {
      key: k,
      label: str(f.label) || FEATURE_LABELS[k],
      mode: featureModeOf(f.mode),
      admins: emailsOf(f.admins),
      updatedBy: str(f.updated_by),
      updatedAt: str(f.updated_at),
    };
  });
}

const MEMBERS_SEE: Record<FeatureKey, string> = {
  foryou: 'members see the previous For You page',
  whatsnew: "members see no What's New entry; its links open the usual home",
  ocean: 'members see the Ocean Coming Soon page',
};

const whoSees = (row: Row): string => {
  switch (row.mode) {
    case 'everyone': return 'Open to every signed-in account.';
    case 'admins': return `Every admin sees it with a “Preview” badge; ${MEMBERS_SEE[row.key]}.`;
    case 'selected': return `Super admins and ${row.admins.length || 'no'} picked admin${row.admins.length === 1 ? '' : 's'} `
      + `see it with a “Preview” badge; ${MEMBERS_SEE[row.key]}.`;
    default: return `Only super admins see it, with a “Super admin preview” badge; ${MEMBERS_SEE[row.key]}.`;
  }
};

const MODE_OPTIONS = (['superadmins', 'selected', 'admins', 'everyone'] as FeatureMode[])
  .map(m => ({ value: m, label: FEATURE_MODE_LABELS[m] }));

const modeTag = (m: FeatureMode) => (m === 'everyone' ? <Tag tone="green">Live for everyone</Tag>
  : m === 'superadmins' ? <Tag tone="violet">Super admin preview</Tag> : <Tag tone="amber">Preview</Tag>);

const FeatureRow: React.FC<{
  row: Row; admins: AdminOption[]; listState?: AdminListState; onSaved: (row: Row, mode: FeatureMode) => void;
  onError: (text: string) => void;
}> = ({ row, admins, listState, onSaved, onError }) => {
  const [mode, setMode] = useState<FeatureMode>(row.mode);
  const [picked, setPicked] = useState<string[]>(row.admins);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  // A fresh server answer resets the draft (keyed by value: rows are rebuilt each render).
  const savedKey = `${row.mode}|${row.admins.join(',')}|${row.updatedAt}`;
  useEffect(() => { setMode(row.mode); setPicked(row.admins); setConfirm(false); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedKey]);

  const dirty = mode !== row.mode || (mode === 'selected' && !sameList(picked, row.admins));
  const save = async () => {
    if (mode === 'everyone' && !confirm) { setConfirm(true); return; }
    setConfirm(false); setBusy(true);
    const err = await faSet(row.key, mode, picked);
    setBusy(false);
    if (err) { onError(`${row.label}: ${err}`); return; }
    onSaved(row, mode);
  };
  const discard = () => { setMode(row.mode); setPicked(row.admins); setConfirm(false); };

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex flex-col min-w-[12rem] flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-grayscale-1300">{row.label}</span>
            {modeTag(row.mode)}
          </span>
          <span className="text-xs text-grayscale-1100">{whoSees(row)}</span>
          <ChangedLine by={row.updatedBy} at={row.updatedAt} isDefault={row.mode === 'superadmins'} />
        </div>
        <a href={FEATURE_OPEN_URL[row.key]} target="_blank" rel="noopener noreferrer"
          title={`Open ${row.label} in the app (new tab)`}
          className="py-1 px-2.5 text-xs rounded-full font-grotesk font-medium whitespace-nowrap border
            border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200">
          Open page ↗
        </a>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <ModeSwitch<FeatureMode> label={`Who can open ${row.label}`} value={mode} options={MODE_OPTIONS}
          disabled={busy} onChange={m => { setMode(m); setConfirm(false); }} />
        {dirty && !confirm && (
          <>
            <Btn small kind="primary" disabled={busy || (mode === 'selected' && !picked.length)} onClick={save}
              title={mode === 'selected' && !picked.length ? 'Pick at least one admin' : undefined}>
              {busy ? 'Saving…' : 'Save'}
            </Btn>
            <Btn small disabled={busy} onClick={discard}>Cancel</Btn>
          </>
        )}
      </div>
      {mode === 'selected' && (
        <div className="pl-1">
          <AdminPicker admins={admins} value={picked} onChange={setPicked} disabled={busy} listState={listState} />
        </div>
      )}
      {confirm && (
        <div className="flex items-center gap-2 flex-wrap rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
          <span className="text-xs text-grayscale-1200 flex-1 min-w-[14rem]">
            Open <b>{row.label}</b> to every signed-in account? Members get it on their next page load.
          </span>
          <Btn small kind="primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Yes, open to everyone'}</Btn>
          <Btn small disabled={busy} onClick={discard}>Cancel</Btn>
        </div>
      )}
    </div>
  );
};

const FeatureAccessCard: React.FC<{ admins?: AdminOption[]; only?: FeatureKey[] }> = ({ admins, only }) => {
  const [state, setState] = useState<{ loading: boolean; error: string; data: any }>({ loading: true, error: '', data: null });
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const roster = useAdminRoster(!!admins);
  const pickable = admins || roster.admins;
  const listState: AdminListState | undefined = admins ? undefined
    : roster.loading ? 'loading' : roster.error ? 'error' : 'ready';

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: '' }));
    const r = await faGet();
    setState(s => ({ loading: false, error: r.error, data: r.data ?? s.data }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saved = (row: Row, mode: FeatureMode) => {
    setMsg({ kind: 'ok', text: mode === 'everyone'
      ? `${row.label} is open to everyone. Members get it on their next page load.`
      : `${row.label}: ${FEATURE_MODE_LABELS[mode].toLowerCase()}. Members keep the previous version; `
        + 'a page someone already has open switches over at its next server call.' });
    announceFeatureAccessChange();
    load();
  };

  const single = only && only.length === 1 ? only[0] : null;
  const title = single ? `Who can open ${FEATURE_LABELS[single]}` : 'Who can open these pages';
  if (state.loading && !state.data) return <Section title={title}><Loading /></Section>;
  if (state.error && !state.data) {
    return (
      <Section title={title} right={<Btn small onClick={load}>Retry</Btn>}>
        <Msg kind="error">{state.error}</Msg>
      </Section>
    );
  }
  const rows = readRows(state.data).filter(r => !only || only.includes(r.key));

  return (
    <Section title={title} right={<Btn small onClick={load} disabled={state.loading}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        {single
          ? <>Who can open this page while it is tested. Until it is open to <b>Everyone</b>, members keep what
            they had and the server refuses the page’s data for them.</>
          : <>Who can open each page while it is tested. Until a page is open to <b>Everyone</b>, members keep
            what they had and the server refuses the page’s data for them.</>}
        {' '}Only super admins see this tab.
      </p>
      {state.error && <Msg kind="error">Could not refresh: {state.error}</Msg>}
      {listState === 'error' && (
        <div className="flex items-center gap-2 flex-wrap">
          <Msg kind="error">Could not load the admin list: {roster.error}</Msg>
          <Btn small onClick={roster.reload}>Retry</Btn>
        </div>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <div className="flex flex-col divide-y divide-primary-100 border border-primary-200 rounded-lg">
        {rows.map(row => (
          <FeatureRow key={row.key} row={row} admins={pickable} listState={listState} onSaved={saved}
            onError={text => setMsg({ kind: 'error', text })} />
        ))}
      </div>
    </Section>
  );
};

export default FeatureAccessCard;
